import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuthStore } from '../../store/useAuthStore';

interface PasswordForm {
  password: string;
  confirmPassword: string;
}

type Step = 'form' | 'code';

/**
 * Section "Mot de passe" de la page Mon compte (/profil).
 *
 * Le projet Supabase a "Secure password change" activé : changer le mot de passe
 * exige une réauthentification. On utilise donc un flux OTP par email en 2 étapes :
 *   1. L'utilisateur saisit son nouveau mot de passe → reauthenticate() envoie un code par email.
 *   2. L'utilisateur saisit le code reçu → updateUser({ password, nonce }) applique le changement.
 */
export function ProfilePasswordSection() {
  const email = useAuthStore((s) => s.collaborator?.email ?? s.session?.user?.email ?? '');
  const [step, setStep] = useState<Step>('form');
  const [code, setCode] = useState('');
  const [pendingPassword, setPendingPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<PasswordForm>();

  const passwordValue = watch('password');

  // Étape 1 : valider le nouveau mdp côté form, puis demander le code par email.
  async function onSubmitForm(values: PasswordForm) {
    setServerError(null);
    setBusy(true);
    const { error } = await supabase.auth.reauthenticate();
    setBusy(false);
    if (error) {
      setServerError("Impossible d'envoyer le code de confirmation. Réessayez.");
      return;
    }
    setPendingPassword(values.password);
    setStep('code');
  }

  // Étape 2 : appliquer le changement avec le code (nonce).
  async function onConfirmCode() {
    if (!code.trim()) {
      setServerError('Veuillez saisir le code reçu par email.');
      return;
    }
    setServerError(null);
    setBusy(true);
    const { error } = await supabase.auth.updateUser({
      email,
      nonce: code.trim(),
      password: pendingPassword,
    });
    setBusy(false);
    if (error) {
      setServerError('Code invalide ou expiré. Vérifiez le code et réessayez.');
      return;
    }
    // Succès : on réinitialise tout
    reset();
    setCode('');
    setPendingPassword('');
    setStep('form');
    setSuccess(true);
  }

  function cancelCode() {
    setStep('form');
    setCode('');
    setPendingPassword('');
    setServerError(null);
  }

  return (
    <div className="bg-cime-surface rounded-cime border border-neutral-200 p-6 space-y-4">
      <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">
        Mot de passe
      </h2>

      {step === 'form' ? (
        <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4 max-w-md">
          <div className="flex flex-col gap-1">
            <label htmlFor="new-password" className="text-sm font-medium text-neutral-700">
              Nouveau mot de passe
            </label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cime-primary"
              aria-invalid={!!errors.password}
              {...register('password', {
                required: 'Le mot de passe est requis',
                minLength: {
                  value: 12,
                  message: 'Le mot de passe doit contenir au moins 12 caractères',
                },
              })}
            />
            {errors.password ? (
              <p className="text-xs text-red-600" role="alert">
                {errors.password.message}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="confirm-password" className="text-sm font-medium text-neutral-700">
              Confirmer le mot de passe
            </label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cime-primary"
              aria-invalid={!!errors.confirmPassword}
              {...register('confirmPassword', {
                required: 'Veuillez confirmer le mot de passe',
                validate: (v) => v === passwordValue || 'Les mots de passe ne correspondent pas',
              })}
            />
            {errors.confirmPassword ? (
              <p className="text-xs text-red-600" role="alert">
                {errors.confirmPassword.message}
              </p>
            ) : null}
          </div>

          {serverError && (
            <p className="text-xs text-red-600" role="alert">
              {serverError}
            </p>
          )}
          {success && (
            <p className="text-xs text-green-600 font-medium">
              Mot de passe modifié.
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md bg-cime-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden />}
            {busy ? 'Envoi du code…' : 'Modifier le mot de passe'}
          </button>
        </form>
      ) : (
        <div className="space-y-4 max-w-md">
          <p className="text-sm text-neutral-600 leading-relaxed">
            Un code de confirmation a été envoyé à <strong>{email}</strong>.
            Saisissez-le ci-dessous pour valider le changement de mot de passe.
          </p>

          <div className="flex flex-col gap-1">
            <label htmlFor="reauth-code" className="text-sm font-medium text-neutral-700">
              Code de confirmation
            </label>
            <input
              id="reauth-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="rounded-md border border-neutral-300 px-3 py-2 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-cime-primary"
            />
          </div>

          {serverError && (
            <p className="text-xs text-red-600" role="alert">
              {serverError}
            </p>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onConfirmCode}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-md bg-cime-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden />}
              {busy ? 'Validation…' : 'Confirmer'}
            </button>
            <button
              type="button"
              onClick={cancelCode}
              disabled={busy}
              className="text-sm text-neutral-500 hover:underline disabled:opacity-50"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
